import { Routes } from '@angular/router';
import { datasetDetailsResolver } from './resolvers/dataset-details.resolver';
import { modelDetailsResolver } from './resolvers/model-details.resolver';
import { faqDetailResolver } from './resolvers/faq-detail.resolver';
import { userManualResolver } from './resolvers/user-manual.resolver';
import { privacyPolicyResolver } from './resolvers/privacy-policy.resolver';
import { termsResolver } from './resolvers/terms.resolver';
import { globalSearchResultsResolver } from './resolvers/global-search-results.resolver';
import { LandingComponent } from './modules/home/landing/landing.component';
import { HomeComponent } from './modules/home/home/home.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { MyProfileComponent } from './pages/register/my-profile/my-profile.component';
import { ToolkitComponent } from './pages/toolkit/toolkit-listing/toolkit-listing.component';
import { ToolkitDetailsComponent } from './pages/toolkit/toolkit-details/toolkit-details.component';
import { profileDetailResolver } from './resolvers/profile-detail.resolver';
import { usecaseDetailsResolver } from './resolvers/usecase-details.resolver';
import { VerifyEmailComponent } from './components/utility/organization-register/verify-email/verify-email.component';
import { SetupIdentityComponent } from './components/utility/organization-register/setup-identity/setup-identity.component';
import { SetupOrganizationComponent } from './components/utility/organization-register/setup-organization/setup-organization.component';
import { toolkitDetailResolver } from './resolvers/toolkit-detail.resolver';
import { RequestSentComponent } from './components/utility/request-sent/request-sent.component';
import { LoadingPageComponent } from './components/utility/loading-page/loading-page.component';
import { SitemapComponent } from './pages/sitemap/sitemap.component';
import { routes } from './constants/routes';
import { resourcesDetailsResolver } from './resolvers/resources-details.resolver';
import { LoginOptionsComponent } from './pages/login/login-options/login-options.component';
import { apiIntegrationDetailResolver } from './resolvers/api-integration-details.resolver';
import { userDetailsResolver } from './resolvers/user-details-resolvers';
import { orgDetailsResolver } from './resolvers/org-details.resolver';
import { metadataStandardsResolver } from './resolvers/metadata-standards.resolvers';
import { VerifyEmailOtpComponent } from './components/utility/organization-register/verify-email-otp/verify-email-otp.component';
import { RegisterStep1Component } from './pages/register/register-step-1/register-step-1.component';
import { RegisterStep2Component } from './pages/register/register-step-2/register-step-2.component';
import { UserIdentitySetupComponent } from './pages/register/user-identity-setup/user-identity-setup.component';
import { competitionDetailsResolver } from './resolvers/competition-detail.resolver';
import { notebookContentResolver } from './resolvers/notebook-content.resolver';
import { SsoLoginComponent } from './components/utility/sso-login/sso-login.component';
import { loginGuard } from './guards/login.guard';
import { articleDetailResolver } from './resolvers/article-detail.resolver';
import { WorkshopComponent } from './modules/home/workshop/workshop.component';
import { WorkshopHomeComponent } from './modules/home/workshop-home/workshop-home.component';
import { RegisterFormComponent } from './components/layout/workshop/register-form/register-form.component';
import { MultiplePhoneSignUpComponent } from './pages/register/multiple-phone-sign-up/multiple-phone-sign-up.component';
import { MultipleEmailSignUpComponent } from './pages/register/multiple-email-sign-up/multiple-email-sign-up.component';
import { NoDataFoundComponent } from './pages/no-data-found/no-data-found.component';

export const appRoutes: Routes = [
  {
    path: '',
    redirectTo: routes.home,
    pathMatch: 'full',
  },
  {
    path: routes.home,
    component: LandingComponent,
    children: [
      {
        path: '',
        component: HomeComponent,
      },
      {
        path: routes.search,
        runGuardsAndResolvers: 'always',
        resolve: { searchResults: globalSearchResultsResolver },
        loadComponent: () =>
          import('./pages/search/search.component').then(
            (c) => c.SearchComponent,
          ),
      },
      {
        path: routes.aboutUs,
        loadComponent: () =>
          import('../app/modules/home/about-us/about-us.component').then(
            (m) => m.AboutUsComponent,
          ),
      },
      {
        path: routes.datasets,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('../app/modules/home/dataset/dataset.component').then(
                (m) => m.DatasetComponent,
              ),
          },
          {
            path: `${routes.details}/:slug`,
            resolve: { datasetDetails: datasetDetailsResolver },
            loadComponent: () =>
              import('../app/pages/dataset/dataset-details/dataset-details.component').then(
                (m) => m.DatasetDetailsComponent,
              ),
          },
        ],
      },
      {
        path: routes.models,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('../app/modules/home/models/models.component').then(
                (m) => m.ModelsComponent,
              ),
          },
          {
            path: `${routes.details}/:slug`,
            resolve: { modelDetails: modelDetailsResolver },
            data: { isModelTryout: false },
            loadComponent: () =>
              import('../app/pages/model/model-details/model-details.component').then(
                (m) => m.ModelDetailsComponent,
              ),
          },
        ],
      },

      {
        path: routes.usecases,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('../app/modules/home/usecases/usecases.component').then(
                (m) => m.UsecasesComponent,
              ),
          },
          {
            path: `${routes.details}/:slug`,
            resolve: { usecaseDetails: usecaseDetailsResolver },
            loadComponent: () =>
              import('../app/pages/usecase/usecase-details/usecase-details.component').then(
                (m) => m.UsecaseDetailsComponent,
              ),
          },
        ],
      },
      {
        path: routes.catalogue,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('./modules/home/catalogue/catalogue.component').then(
                (m) => m.CatalogueComponent,
              ),
          },
        ],
      },
      {
        path: routes.notebook,
        resolve: { notebookContent: notebookContentResolver },
        loadComponent: () =>
          import('./modules/home/notebook/notebook.component').then(
            (m) => m.NotebookComponent,
          ),
      },
      {
        path: routes.termsNCondition,
        resolve: { termsAndConditionDetails: termsResolver },
        loadComponent: () =>
          import('../app/components/layout/terms-n-conditions/terms-n-conditions.component').then(
            (m) => m.TermsNConditionsComponent,
          ),
      },
      {
        path: routes.individualPrivacy,
        resolve: { privacyDetails: privacyPolicyResolver },
        loadComponent: () =>
          import('../app/components/layout/individual-register-terms/individual-register-terms.component').then(
            (m) => m.IndividualRegisterTermsComponent,
          ),
      },
      {
        path: routes.orgPrivacy,
        resolve: { privacyDetails: privacyPolicyResolver },
        loadComponent: () =>
          import('../app/components/layout/organisation-regiter-terms/organisation-regiter-terms.component').then(
            (m) => m.OrganisationRegiterTermsComponent,
          ),
      },
      {
        path: routes.privacyPolicy,
        resolve: { privacyDetails: privacyPolicyResolver },
        loadComponent: () =>
          import('../app/components/layout/privacy-policy/privacy-policy.component').then(
            (m) => m.PrivacyPolicyComponent,
          ),
      },
      {
        path: routes.faqs,
        resolve: { faqDetails: faqDetailResolver },
        loadComponent: () =>
          import('../app/components/layout/faqs/faqs.component').then(
            (m) => m.FaqsComponent,
          ),
      },
      {
        path: routes.securityMeasures,
        resolve: { userManualDetails: userManualResolver },
        loadComponent: () =>
          import('../app/components/layout/security/security.component').then(
            (m) => m.SecurityComponent,
          ),
      },
      {
        path: routes.userManual,
        resolve: { userManualDetails: userManualResolver },
        loadComponent: () =>
          import('../app/components/layout/user-manual/user-manual.component').then(
            (m) => m.UserManualComponent,
          ),
      },
      {
        path: `${routes.userManual}/${routes.metadataStandards}`,
        resolve: { metadataStandardsDetails: metadataStandardsResolver },
        loadComponent: () =>
          import('../app/components/layout/metadata-standards/metadata-standards.component').then(
            (m) => m.MetadataStandardsComponent,
          ),
      },
      {
        path: routes.resources,
        loadComponent: () =>
          import('../app/modules/home/resources/resources.component').then(
            (m) => m.ResourcesComponent,
          ),
      },
      {
        path: `${routes.resources_details}/:slug`,
        resolve: { resourcesDetails: resourcesDetailsResolver },
        loadComponent: () =>
          import('../app/pages/resources/resources-details/resources-details.component').then(
            (m) => m.ResourcesDetailsComponent,
          ),
      },

      {
        path: `${routes.apiIntegrationDetails}/:slug`,
        resolve: { toolkitDetails: apiIntegrationDetailResolver },
        loadComponent: () =>
          import('../app/pages/resources/api-integration-details/api-integration-details.component').then(
            (m) => m.ApiIntegrationDetailsComponent,
          ),
      },
      {
        path: routes.toolkit,
        children: [
          {
            path: '',
            redirectTo: routes.all,
            pathMatch: 'full',
          },
          {
            path: ':type',
            loadComponent: () =>
              import('./pages/toolkit/toolkit-listing/toolkit-listing.component').then(
                (m) => ToolkitComponent,
              ),
          },
          {
            path: `${routes.details}/:slug`,
            resolve: { toolkitDetails: toolkitDetailResolver },
            loadComponent: () =>
              import('../app/pages/toolkit/toolkit-details/toolkit-details.component').then(
                (m) => ToolkitDetailsComponent,
              ),
          },
        ],
      },
      {
        path: routes.article,
        children: [
          {
            path: routes.categoryListing,
            loadComponent: () =>
              import('../app/modules/home/article-category-listing/article-category-listing.component').then(
                (m) => m.ArticleCategoryListingComponent,
              ),
          },
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('../app/modules/home/article/article.component').then(
                (m) => m.ArticleComponent,
              ),
          },
          {
            path: `${routes.details}/:id`,
            resolve: { articleDetails: articleDetailResolver },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            loadComponent: () =>
              import('../app/pages/article/article-details/article-details.component').then(
                (m) => m.ArticlesDetailsComponent,
              ),
          },
        ],
      },
      {
        path: routes.publicProfile,
        children: [
          {
            path: `${routes.user}/:userId`,
            resolve: { userDetails: userDetailsResolver },
            runGuardsAndResolvers: 'paramsChange',
            loadComponent: () =>
              import('../app/pages/public-profile/user/profile-page/profile-page.component').then(
                (c) => c.ProfilePageComponent,
              ),
          },
          {
            path: `${routes.org}/:orgId`,
            resolve: { orgDetails: orgDetailsResolver },
            runGuardsAndResolvers: 'paramsChange',
            loadComponent: () =>
              import('./pages/public-profile/organisation/profile-page/profile-page.component').then(
                (c) => c.ProfilePageComponent,
              ),
          },
        ],
      },
      {
        path: `${routes.rankings}`,
        children: [
          {
            path: '',
            redirectTo: 'contributor',
            pathMatch: 'full',
          },
          {
            path: ':type',
            loadComponent: () =>
              import('../app/pages/ranking/ranking/ranking.component').then(
                (m) => m.RankingComponent,
              ),
          },
          {
            path: `organization/${routes.members}`,
            loadComponent: () =>
              import('../app/pages/ranking/members/members.component').then(
                (m) => m.MembersComponent,
              ),
          },
        ],
      },

      {
        path: routes.competitions,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('./pages/competition/competition-listing/competition-listing.component').then(
                (m) => m.CompetitionListingComponent,
              ),
          },
          {
            path: `${routes.details}/:slug`,
            resolve: { competitionDetails: competitionDetailsResolver },
            runGuardsAndResolvers: 'always',
            loadComponent: () =>
              import('./pages/competition/competition-details/competition-details.component').then(
                (m) => m.CompetitionDetailsComponent,
              ),
          },
        ],
      },
      {
        path: routes.organization,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('./pages/organization/organization-listing/organization-listing.component').then(
                (m) => m.OrganizationListingComponent,
              ),
          },
        ],
      },
      {
        path: routes.modelTryout,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.all,
          },
          {
            path: ':sector',
            loadComponent: () =>
              import('./pages/model-tryout/model-tryout-listing/model-tryout-listing.component').then(
                (m) => m.ModelTryoutListingComponent,
              ),
          },
          {
            path: `${routes.details}/:slug`,
            resolve: { modelDetails: modelDetailsResolver },
            data: { isModelTryout: true },
            loadComponent: () =>
              import('../app/pages/model-tryout/model-tryout-details/model-tryout-details.component').then(
                (m) => m.ModelTryoutDetailsComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: routes.account,
    children: [
      {
        path: routes.register,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: routes.step1,
          },
          {
            path: routes.step1,
            component: RegisterStep1Component,
          },
          {
            path: routes.step2,
            component: RegisterStep2Component,
          },
        ],
      },
      {
        path: routes.login,
        canActivate: [loginGuard],
        runGuardsAndResolvers: 'always',
        loadComponent: () =>
          import('../app/pages/login/login.component').then(
            (m) => m.LoginComponent,
          ),
        children: [
          {
            path: '',
            component: LoginOptionsComponent,
          },
        ],
      },
      {
        path: routes.user,
        loadComponent: () =>
          import('../app/pages/login/login.component').then(
            (m) => m.LoginComponent,
          ),
        children: [
          {
            path: routes.login,
            canActivate: [loginGuard],
            runGuardsAndResolvers: 'always',
            component: LoginOptionsComponent,
          },
          {
            path: `${routes.login}/${routes.sso}`,
            component: SsoLoginComponent,
          },
          {
            path: routes.verifyEmail,
            component: VerifyEmailComponent,
          },
          {
            path: routes.verifyOtp,
            component: VerifyEmailOtpComponent,
          },
          {
            path: routes.userIdentitySetup,
            component: UserIdentitySetupComponent,
          },
        ],
      },
      {
        path: routes.signUp,
        loadComponent: () =>
          import('../app/pages/register/register.component').then(
            (m) => m.RegisterComponent,
          ),
        children: [
          {
            path: routes.myProfile,
            resolve: { profileDetails: profileDetailResolver },
            component: MyProfileComponent,
          },
          {
            path: routes.multiplePhoneSignUp,
            component: MultiplePhoneSignUpComponent,
          },
          {
            path: routes.multipleEmailSignUp,
            component: MultipleEmailSignUpComponent,
          },
          {
            path: routes.verifyOrganizationEmail,
            component: VerifyEmailComponent,
          },
          {
            path: routes.setupOrg,
            component: SetupOrganizationComponent,
          },
          {
            path: routes.requestAccess,
            component: RequestSentComponent,
          },
        ],
      },
    ],
  },
  {
    path: routes.support,
    component: LandingComponent,
    children: [
      {
        path: routes.contactUs,
        loadComponent: () =>
          import('../app/pages/contact-us/contact-us.component').then(
            (m) => m.ContactUsComponent,
          ),
      },
      {
        path: routes.trackGrievance,
        loadComponent: () =>
          import('../app/pages/grievance/grievance.component').then(
            (m) => m.GrievanceComponent,
          ),
      },
    ],
  },

  {
    path: routes.authRedirectPage,
    component: LoadingPageComponent,
  },
  {
    path: routes.sitemap,
    component: SitemapComponent,
  },

  {
    path: routes.workshop,
    component: WorkshopComponent,
    children: [
      {
        path: '',
        component: WorkshopHomeComponent,
      },
      {
        path: routes.register_university,
        component: RegisterFormComponent,
      },
    ],
  },
  {
    path: '404',
    component: NoDataFoundComponent
  },
  {
    path: '**',
    redirectTo: routes.home,
  },
];
